/**
 * Relatos crypto core — invite capability tokens BOUND to an invitee pubkey.
 *
 * Phase-1 fix: tokens are not pure bearer tokens. The payload containing the epoch
 * secret is NIP-44-encrypted from the admin key to the invitee key, the invitee
 * pubkey is committed in the signed body, and the whole token is Schnorr-signed by
 * the admin. Consequences, each covered by a test:
 * - A leaked token redeemed by anyone but the bound invitee fails (they cannot
 *   decrypt `ek`, and the pubkey check rejects them first).
 * - A tampered token fails signature verification.
 * - An expired token fails the expiry check.
 *
 * Token wire format: base64url(JSON(InviteToken)) — embeddable in an invite link.
 */
import { schnorr } from '@noble/curves/secp256k1.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes, utf8ToBytes } from '@noble/hashes/utils.js';
import { verifyEvent } from 'nostr-tools';
import type { EventTemplate, NostrEvent } from 'nostr-tools';
import { peerEncrypt, peerDecrypt, selfPubkey } from './nip44';
import type { PeerCrypto } from '../client';

export interface InviteToken {
  v: 1;
  /** board id (hex) */
  board: string;
  /** epoch the invite grants */
  epoch: number;
  /** NIP-44(admin -> invitee) of { key, salts } — the epoch secret bundle */
  ek: string;
  /** bound invitee pubkey */
  inv: string;
  /** expiry, unix seconds */
  exp: number;
  /** admin (board owner) pubkey */
  adm: string;
  /** schnorr signature by adm over the canonical body */
  sig: string;
}

/** What the invitee receives after redemption. */
export interface InviteGrant {
  boardId: string;
  adminPubkey: string;
  epoch: number;
  epochKey: string;
}

export class InviteError extends Error {}

function canonicalBody(t: Omit<InviteToken, 'sig'>): Uint8Array {
  return utf8ToBytes(`relatos-invite:v1:${t.board}:${t.epoch}:${t.inv}:${t.exp}:${t.ek}`);
}

export function createInvite(params: {
  adminSk: string;
  boardId: string;
  epoch: number;
  epochKey: string;
  inviteePk: string;
  expiresAt: number;
}): InviteToken {
  const ek = peerEncrypt(params.adminSk, params.inviteePk, { key: params.epochKey });
  const body: Omit<InviteToken, 'sig'> = {
    v: 1,
    board: params.boardId,
    epoch: params.epoch,
    ek,
    inv: params.inviteePk,
    exp: params.expiresAt,
    adm: selfPubkey(params.adminSk),
  };
  const digest = sha256(canonicalBody(body));
  const sig = bytesToHex(schnorr.sign(digest, hexToBytes(params.adminSk)));
  return { ...body, sig };
}

export function verifyInvite(token: InviteToken, now: number = Math.floor(Date.now() / 1000)): void {
  if (token.v !== 1) throw new InviteError('unknown token version');
  if (token.exp <= now) throw new InviteError('invite expired');
  const { sig, ...body } = token;
  const digest = sha256(canonicalBody(body));
  if (!schnorr.verify(hexToBytes(sig), digest, hexToBytes(token.adm))) {
    throw new InviteError('bad admin signature');
  }
}

/** Redeem a token. Only the bound invitee's secret key can succeed. */
export function redeemInvite(inviteeSk: string, token: InviteToken, now?: number): InviteGrant {
  verifyInvite(token, now);
  const inviteePk = selfPubkey(inviteeSk);
  if (inviteePk !== token.inv) throw new InviteError('token is bound to a different pubkey');
  const secret = peerDecrypt<{ key: string }>(inviteeSk, token.adm, token.ek);
  if (typeof secret?.key !== 'string' || secret.key.length !== 64) {
    throw new InviteError('malformed epoch secret');
  }
  return { boardId: token.board, adminPubkey: token.adm, epoch: token.epoch, epochKey: secret.key };
}

// --- v2: event-shaped tokens for capability signers (NIP-07 nip44) ----------
//
// v1 tokens are Schnorr-signed over a custom digest — an extension can't do
// that. v2 makes the token a plain signed Nostr event (kind 1117, never
// published): signEvent covers the signature, peer crypto covers the secret.
// Same binding/expiry guarantees as v1.

export const INVITE_TOKEN_KIND = 1117;

export async function createInviteV2(params: {
  crypto: PeerCrypto;
  signEvent: (tmpl: EventTemplate) => Promise<NostrEvent>;
  boardId: string;
  epoch: number;
  epochKey: string;
  inviteePk: string;
  expiresAt: number;
}): Promise<NostrEvent> {
  const ek = await params.crypto.encryptTo(params.inviteePk, { key: params.epochKey });
  return params.signEvent({
    kind: INVITE_TOKEN_KIND,
    created_at: Math.floor(Date.now() / 1000),
    tags: [
      ['inv', params.inviteePk],
      ['exp', String(params.expiresAt)],
      ['board', params.boardId],
      ['epoch', String(params.epoch)],
    ],
    content: ek,
  });
}

const tag = (t: NostrEvent, k: string) => t.tags.find(x => x[0] === k)?.[1];

export function verifyInviteV2(token: NostrEvent, now: number = Math.floor(Date.now() / 1000)): void {
  if (token.kind !== INVITE_TOKEN_KIND) throw new InviteError('not a v2 invite token');
  if (!verifyEvent(token)) throw new InviteError('bad admin signature');
  if (Number(tag(token, 'exp') ?? 0) <= now) throw new InviteError('invite expired');
  if (!tag(token, 'inv') || !tag(token, 'board') || !tag(token, 'epoch')) throw new InviteError('malformed invite');
}

export async function redeemInviteV2(crypto: PeerCrypto, inviteePk: string, token: NostrEvent, now?: number): Promise<InviteGrant> {
  verifyInviteV2(token, now);
  if (tag(token, 'inv') !== inviteePk) throw new InviteError('token is bound to a different pubkey');
  const secret = await crypto.decryptFrom(token.pubkey, token.content) as { key?: string };
  if (typeof secret?.key !== 'string' || secret.key.length !== 64) {
    throw new InviteError('malformed epoch secret');
  }
  return { boardId: tag(token, 'board')!, adminPubkey: token.pubkey, epoch: Number(tag(token, 'epoch')), epochKey: secret.key };
}

// --- wire format -----------------------------------------------------------

// atob/btoa exist in browsers AND in Node >= 16 — no Buffer, no polyfill.
const B64U = {
  enc(s: string): string {
    return btoa(unescape(encodeURIComponent(s))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  },
  dec(s: string): string {
    return decodeURIComponent(escape(atob(s.replace(/-/g, '+').replace(/_/g, '/'))));
  },
};

export function encodeInvite(token: InviteToken): string {
  return 'relatos1' + B64U.enc(JSON.stringify(token));
}

export function encodeInviteV2(token: NostrEvent): string {
  return 'relatos2' + B64U.enc(JSON.stringify(token));
}

export function decodeInvite(s: string): InviteToken {
  if (!s.startsWith('relatos1')) throw new InviteError('not a relatos invite');
  const t = JSON.parse(B64U.dec(s.slice(8))) as InviteToken;
  if (t.v !== 1 || !t.board || !t.ek || !t.inv || !t.sig) throw new InviteError('malformed invite');
  return t;
}

export function decodeInviteV2(s: string): NostrEvent {
  if (!s.startsWith('relatos2')) throw new InviteError('not a v2 relatos invite');
  return JSON.parse(B64U.dec(s.slice(8))) as NostrEvent;
}
